import React, { useState } from 'react';
import { useEmailOpens } from '../hooks/useEmailOpens';
import { Calendar, Search, Download, RefreshCw } from 'lucide-react';
import { formatNumber } from '../utils/formatters';
import { useSidebarState } from '../hooks/useSidebarState';

export function EmailOpensPage() {
  const [page, setPage] = useState(1);
  const [searchEmail, setSearchEmail] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const { isCollapsed } = useSidebarState();
  
  const { stats, loading, error, refresh } = useEmailOpens({
    page,
    pageSize: 10,
    email: searchEmail || undefined,
    realtimeUpdates: true
  });

  if (error) {
    return <div className="text-red-500">Error: {error}</div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0B1A] text-white p-8">
      <div className={`mx-auto transition-all duration-300 ${
        isCollapsed 
          ? 'max-w-[1400px] px-4' 
          : 'max-w-[1200px]'
      }`}>
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl font-bold">Relatório de E-mails</h1>
            <p className="text-gray-400 text-sm">Acompanhamento detalhado de métricas de e-mail</p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={refresh}
              className="p-2 bg-[#1C1D2C] text-gray-300 rounded-lg hover:bg-[#2C2D3C] transition-colors"
              title="Atualizar dados"
              disabled={loading}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button className="px-4 py-2 bg-[#1C1D2C] text-gray-300 rounded-lg flex items-center gap-2 hover:bg-[#2C2D3C] transition-colors">
              <Download className="w-4 h-4" />
              Exportar dados
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <div className="bg-[#1C1D2C] p-6 rounded-xl">
            <p className="text-gray-400 text-sm">Total de E-mails</p>
            <p className="text-2xl font-bold mt-2">{formatNumber(stats.totalEmails)}</p>
          </div>
          <div className="bg-[#1C1D2C] p-6 rounded-xl">
            <p className="text-gray-400 text-sm">E-mails Únicos</p>
            <p className="text-2xl font-bold mt-2">{formatNumber(stats.uniqueEmails)}</p>
          </div>
          <div className="bg-[#1C1D2C] p-6 rounded-xl">
            <p className="text-gray-400 text-sm">Total de Cliques</p>
            <p className="text-2xl font-bold mt-2">
              {formatNumber(stats.totalClicks)}
            </p>
          </div>
          <div className="bg-[#1C1D2C] p-6 rounded-xl">
            <p className="text-gray-400 text-sm">Taxa de Abertura</p>
            <p className="text-2xl font-bold mt-2">
              {stats.openRate.toFixed(1)}%
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar por e-mail..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="w-full bg-[#1C1D2C] text-white rounded-lg pl-10 pr-4 py-2 border border-gray-700 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div className="relative">
            <Calendar className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-[#1C1D2C] text-white rounded-lg pl-10 pr-4 py-2 border border-gray-700 focus:border-indigo-500 focus:outline-none appearance-none"
            >
              <option value="all">Todos os períodos</option>
              <option value="today">Hoje</option>
              <option value="week">Última semana</option>
              <option value="month">Último mês</option>
            </select>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-[#1C1D2C] rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4">Resumo</h2>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-400">Taxa de Cliques</p>
              <p className="text-2xl font-bold mt-1">{stats.clickThroughRate.toFixed(1)}%</p>
              <p className="text-sm text-green-400 mt-1">+5.2% vs. período anterior</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Média de Cliques por E-mail</p>
              <p className="text-2xl font-bold mt-1">{stats.averageClicks.toFixed(1)}</p>
              <p className="text-sm text-green-400 mt-1">+2.8% vs. período anterior</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Taxa de Engajamento</p>
              <p className="text-2xl font-bold mt-1">
                {((stats.clickThroughRate + stats.openRate) / 2).toFixed(1)}%
              </p>
              <p className="text-sm text-green-400 mt-1">+4.1% vs. período anterior</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}